import { FC, useState, useMemo, useContext, useEffect, useRef } from 'react';
import { Box, Divider, Flex, Skeleton, Text } from 'src/components/ui';
import { v4 as uuid } from 'uuid';
import UserAvatar from 'src/components/common/UserAvatar';
import { SelectSearchCreateableInput } from 'src/components/common/form';
import GroupFormModal from 'src/components/groups/GroupFormModal';
import Overlay from 'src/components/common/Overlay';
import { CampaignContext } from 'src/contexts';
import useGroups from 'src/hooks/useGroups';
import useToggleWithPayload from 'src/hooks/useToggleWithPayload';
import { humanizeTime, upsertElem } from 'src/lib/utils';
import { IGroup, IGroupForm } from 'src/lib/schemas';
import { updateGroup } from 'src/services/config';
import { toastError } from 'src/services/toast';

interface CampaignHeaderProps {
	campaignDetail: {
		title: string;
		group?: string;
		lastEditTime?: Date;
		lastEditBy?: string;
	};
	onGroupChange: (val: string) => void;
	scrappedDataLoading?: boolean;
}

const CampaignHeader: FC<CampaignHeaderProps> = ({
	campaignDetail,
	onGroupChange,
	scrappedDataLoading,
}) => {
	const [isCreating, setIsCreating] = useState(false);
	const [lastEditTimeMessage, setLastEditTimeMessage] = useState('');
	const { isFetching } = useContext(CampaignContext);
	const formToggle = useToggleWithPayload<IGroupForm>();
	const { title, group, lastEditTime, lastEditBy } = campaignDetail;
	const { data: groups, isLoading, refetch } = useGroups();

	const GROUP_OPTIONS = useMemo(
		() =>
			(groups || []).map((g) => ({
				value: g.id,
				label: g.name,
			})),
		[groups],
	);

	const handleCreateGroup = async (payload: IGroupForm) => {
		setIsCreating(true);
		try {
			if (!payload.id) {
				payload.id = uuid();
			}
			onGroupChange(payload.id);
			await updateGroup(upsertElem(groups || [], payload as IGroup));
			refetch();
		} catch (error: any) {
			toastError(error);
		}
		setIsCreating(false);
	};

	const handleCreateGroupOption = async (name: string) => {
		if (!name.trim()) {
			formToggle.onOpen();
			return;
		}
		await handleCreateGroup({ name });
	};

	useEffect(() => {
		if (!lastEditTime) return;

		const time = `Last edit ${humanizeTime(lastEditTime)} by`;
		setLastEditTimeMessage(time);

		const intervalId = setInterval(() => {
			const time = `Last edit ${humanizeTime(lastEditTime)} by`;
			setLastEditTimeMessage(time);
		}, 1000);

		return () => {
			clearInterval(intervalId);
		};
	}, [lastEditTime]);

	const defaultGroup = useMemo(() => {
		const selectedGroup =
			GROUP_OPTIONS?.find((g) => g.value === group) ||
			GROUP_OPTIONS?.find((g) => g.label === 'Default') ||
			GROUP_OPTIONS[0];

		return selectedGroup?.value;
	}, [GROUP_OPTIONS]);

	const showLoading =
		isCreating || isLoading || isFetching || scrappedDataLoading;

	const groupValue = group ?? defaultGroup;

	// Scroll-based hide state
	const [isHeaderVisible, setIsHeaderVisible] = useState(true);
	const lastScrollYRef = useRef(0);

	useEffect(() => {
		const handleScroll = () => {
			const currentScrollY = window.scrollY;
			const lastScrollY = lastScrollYRef.current;
			const isMobile = window.innerWidth < 768;

			// Only hide on mobile (screen width < 768px)
			if (isMobile) {
				// Scrolling down and past 50px
				if (currentScrollY > lastScrollY && currentScrollY > 50) {
					if (isHeaderVisible) {
						setIsHeaderVisible(false);
					}
				}
				// Scrolling up
				else if (currentScrollY < lastScrollY) {
					if (!isHeaderVisible) {
						setIsHeaderVisible(true);
					}
				}
			} else {
				if (!isHeaderVisible) {
					setIsHeaderVisible(true); // Always visible on desktop
				}
			}

			lastScrollYRef.current = currentScrollY;
		};

		window.addEventListener('scroll', handleScroll, { passive: true });
		return () => window.removeEventListener('scroll', handleScroll);
	}, [isHeaderVisible]);

	useEffect(() => {
		if (!showLoading && !group) {
			onGroupChange(defaultGroup);
		}
	}, [showLoading]);

	// Debug logging for CampaignHeader
	console.log('📋 CampaignHeader Render:', {
		title,
		group,
		groupValue,
		defaultGroup,
		showLoading,
		screenWidth: typeof window !== 'undefined' ? window.screen.width : 0,
		windowWidth: typeof window !== 'undefined' ? window.innerWidth : 0,
		isMobileDevice: typeof window !== 'undefined' && window.innerWidth < 768,
	});

	return (
		<Box
			display="none"
		>
			<Overlay
				display="none"
				h="calc(100% + 1px)"
				zIndex={1600}
				id="campaign-header-overlay"
			/>
			<Flex
				gap={{ base: 2, md: 4 }}
				alignItems={{ base: 'flex-start', md: 'center' }}
				position={{ base: 'relative', md: 'fixed' }}
				flexDirection={{ base: 'column', md: 'row' }}
				flexWrap={{ base: 'nowrap', md: 'wrap' }}
			>
				{showLoading ? (
					<Skeleton height="25px" width="270px" />
				) : (
					<Text
						fontSize={{ base: 'md', md: 'md' }}
						fontWeight="semibold"
						textDecor="underline"
						minW="60px"
						maxW={{ base: '100%', md: '400px' }}
						w={{ base: '100%', md: 'auto' }}
						isTruncated
					>
						{title.length > 50 ? `${title.slice(0, 50)}...` : title}
					</Text>
				)}

				<Divider orientation="vertical" display={{ base: 'none', md: 'block' }} />
				<Box w={{ base: '100%', md: '250px' }}>
					<SelectSearchCreateableInput
						name="g"
						placeholder="Select Group"
						options={GROUP_OPTIONS}
						value={showLoading ? undefined : groupValue}
						onChange={(val) => {
							if (typeof val !== 'string') return;
							onGroupChange(val);
						}}
						onCreateOption={handleCreateGroupOption}
						isLoading={isCreating || isLoading || isFetching}
						valuePrefix="Group: "
					/>
				</Box>
				<Flex alignItems="center" gap={1} w={{ base: '100%', md: 'auto' }}>
					{lastEditTime && <Text color="#A0AEC0" fontSize={{ base: 'xs', md: 'sm' }}>{lastEditTimeMessage}</Text>}
					{lastEditBy && <UserAvatar name={lastEditBy} />}
				</Flex>
			</Flex>
			<GroupFormModal
				isOpen={formToggle.isOpen}
				onClose={formToggle.onClose}
				onSubmit={handleCreateGroup}
				initialValues={formToggle.payload}
				title={`${formToggle.payload ? 'Edit' : 'Add'} Group`}
			/>
		</Box>
	);
};

export default CampaignHeader;
